import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { initiativePath } from '../../lib/initiatives';
import { loadStatesById } from '../../lib/initiative-streams';
import { ApiError, apiFetch, v1Path } from '../../lib/api';
import { parsePeekValue, UUID_RE, type StateDto, type WorkItemFull } from '../../lib/work-types';
import WorkItemDetail from './WorkItemDetail';
import ServiceRequestDetail from './ServiceRequestDetail';
import type { ServiceRequest } from '../../lib/service-requests';
import { useOverlays } from './overlay-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../ui/dialog';
import { LoadingRows, WorkPageError } from './page-states';

type Props = {
  projectId?: string;
  /** Work-item keys in visual order, used for arrow-key peek navigation. */
  itemKeys?: string[];
  /** Use the viewport. Service requests need the room; other work stays in the compact peek. */
  expanded?: boolean;
  /**
   * Opens this item in a popup and leaves the page where it is.
   * Pass null while the popup is closed. Omit it to follow the address bar.
   */
  pinnedKey?: string | null;
  onPinnedClose?: () => void;
};

export default function Peek({ projectId, itemKeys = [], expanded = false, pinnedKey, onPinnedClose }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isBlocked } = useOverlays();
  const detached = pinnedKey !== undefined;
  const rawPeek = detached ? pinnedKey : searchParams.get('peek');
  const peek = parsePeekValue(rawPeek);
  const [item, setItem] = useState<WorkItemFull | null>(null);
  const [serviceRequest, setServiceRequest] = useState<ServiceRequest | null>(null);
  const [states, setStates] = useState<StateDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const close = useCallback(() => {
    if (detached) {
      onPinnedClose?.();
      return;
    }
    const id = item?.id ?? null;
    const next = new URLSearchParams(searchParams);
    next.delete('peek');
    setSearchParams(next, { replace: true });
    if (id) {
      window.setTimeout(() => {
        document.getElementById(`issue-${id}`)?.focus();
      }, 0);
    }
  }, [detached, item?.id, onPinnedClose, searchParams, setSearchParams]);

  const load = useCallback(async () => {
    if (!peek) {
      setItem(null);
      setServiceRequest(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const isKey = peek.includes('-') && !UUID_RE.test(peek);
      let path: string | null = null;
      if (detached && isKey) {
        path = `/work-items/${encodeURIComponent(peek)}`;
      } else if (isKey && projectId) {
        path = `/projects/${projectId}/work-items/by-key/${encodeURIComponent(peek)}`;
      } else if (isKey) {
        path = `/work-items/${encodeURIComponent(peek)}`;
      } else if (projectId) {
        path = `/projects/${projectId}/work-items/${peek}`;
      }
      if (!path) {
        setItem(null);
        setError('Work item not found.');
        return;
      }
      const full = await apiFetch<WorkItemFull>(v1Path(path));
      if (!detached && projectId && full.project_id !== projectId) {
        setItem(null);
        setError('This work item is not in this stream.');
        return;
      }
      if (!detached && full.type === 'initiative') {
        navigate(initiativePath(full.id));
        return;
      }
      const stateRes =
        full.viewer_is_member === false
          ? { items: Array.from((await loadStatesById([full.project_id])).values()) }
          : await apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${full.project_id}/states`));
      setItem(full);
      setServiceRequest(
        full.type === 'service_request'
          ? await apiFetch<ServiceRequest>(
              v1Path(`/projects/${full.project_id}/service-requests/${full.id}`),
            )
          : null,
      );
      setStates(stateRes.items);
    } catch (err) {
      setItem(null);
      setServiceRequest(null);
      if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
        setError('Work item not found.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load work item');
      }
    } finally {
      setLoading(false);
    }
  }, [detached, navigate, peek, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const goToKey = useCallback(
    (key: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('peek', key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!peek || itemKeys.length < 2) return;
    function onKey(event: KeyboardEvent) {
      if (isBlocked) return;
      if (
        event.key !== 'ArrowDown' &&
        event.key !== 'ArrowUp' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight'
      ) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      const currentKey = item?.key ?? (peek && peek.includes('-') ? peek : null);
      if (!currentKey) return;
      const index = itemKeys.indexOf(currentKey);
      if (index < 0) return;
      const delta = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
      const nextKey = itemKeys[index + delta];
      if (!nextKey) return;
      event.preventDefault();
      goToKey(nextKey);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [peek, item?.key, itemKeys, isBlocked, goToKey]);

  const wide = expanded || (detached && item?.type === 'service_request');

  return (
    <Dialog
      open={Boolean(peek)}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        className={
          wide
            ? 'flex h-[calc(100dvh-1.5rem)] max-h-none w-[calc(100vw-1.5rem)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:rounded-xl'
            : 'flex max-h-[min(52rem,calc(100dvh-2rem))] w-[calc(100vw-2rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:rounded-lg'
        }
        onPointerDownOutside={(event) => {
          // Editor suggestion popups (@mentions) are portaled to <body>; a click on
          // one must not be treated as an outside click that dismisses the peek.
          const target = event.target as Element | null;
          if (isBlocked || target?.closest?.('.react-renderer')) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (isBlocked) event.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">{item?.key ?? 'Work item'}</DialogTitle>
        <DialogDescription className="sr-only">
          Work item details. Escape or the overlay closes this dialog.
        </DialogDescription>
        <div className={wide ? 'min-h-0 flex-1 overflow-y-auto px-8 py-6 pr-14' : 'min-h-0 flex-1 overflow-y-auto px-6 py-6 pr-14'}>
          {loading ? <LoadingRows /> : null}
          <WorkPageError error={error} projectId={projectId} />
          {item && serviceRequest ? (
            <ServiceRequestDetail
              projectId={item.project_id}
              request={serviceRequest}
              states={states}
              onChanged={(next) => {
                setServiceRequest(next);
                setItem(next.work_item);
              }}
              onDeleted={close}
              chrome="peek"
            />
          ) : item ? (
            <WorkItemDetail
              projectId={item.project_id}
              item={item}
              states={states}
              onUpdated={setItem}
              chrome="peek"
              onMarkedDone={close}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
